import api from '@/lib/api';

export interface SystemTag {
  id: string;
  name: string;
  description?: string;
  color: string;
  keywords?: string[];
  usage_count: number;
  last_used_at?: string;
  organization_id?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  // Hierarchical fields
  parent_id?: string;
  tag_level?: number; // 1=parent, 2=child
  parent_name?: string;
  parent_color?: string;
  full_path?: string; // e.g., "Reklamation → Defektes Produkt"
}

export interface TicketSystemTag extends SystemTag {
  confidence_score?: number;
  assigned_by: 'ai' | 'manual';
  assigned_at: string;
}

export interface CreateSystemTagData {
  organizationId: string;
  name: string;
  description?: string;
  keywords?: string[];
}

export interface UpdateSystemTagData {
  name?: string;
  description?: string;
  keywords?: string[];
  color?: string;
  is_active?: boolean;
}

export interface SystemTagsResponse {
  success: boolean;
  data: {
    tags: SystemTag[];
  };
}

export interface TicketSystemTagsResponse {
  success: boolean;
  data: {
    tags: TicketSystemTag[];
  };
}

export interface SystemTagResponse {
  success: boolean;
  data: {
    tag: SystemTag;
  };
  message?: string;
}

export interface AnalyzeTagResponse {
  success: boolean;
  data: {
    tag: SystemTag;
    action: 'use_existing' | 'create_new';
    confidence: number;
  };
  message?: string;
}

class SystemTagsService {
  /**
   * Organizasyonun tüm system tag'lerini getir
   */
  async getOrganizationTags(organizationId: string): Promise<SystemTagsResponse> {
    const response = await api.get<SystemTagsResponse>(`/system-tags/organization/${organizationId}`);
    return response.data;
  }

  /**
   * Ticket'ın system tag'lerini getir
   */
  async getTicketTags(ticketId: string): Promise<TicketSystemTagsResponse> {
    const response = await api.get<TicketSystemTagsResponse>(`/system-tags/ticket/${ticketId}`);
    return response.data;
  }

  /**
   * En çok kullanılan tag'leri getir
   */
  async getTopTags(organizationId: string, limit: number = 10): Promise<SystemTagsResponse> {
    const response = await api.get<SystemTagsResponse>(`/system-tags/organization/${organizationId}/top?limit=${limit}`);
    return response.data;
  }

  /**
   * Yeni system tag oluştur
   */
  async createTag(data: CreateSystemTagData): Promise<SystemTagResponse> {
    const response = await api.post<SystemTagResponse>('/system-tags', data);
    return response.data;
  }

  /**
   * System tag güncelle
   */
  async updateTag(tagId: string, data: UpdateSystemTagData): Promise<SystemTagResponse> {
    const response = await api.put<SystemTagResponse>(`/system-tags/${tagId}`, data);
    return response.data;
  }

  /**
   * System tag sil (soft delete)
   */
  async deleteTag(tagId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/system-tags/${tagId}`);
    return response.data;
  }

  /**
   * Ticket'a system tag ata
   */
  async assignTagToTicket(ticketId: string, systemTagId: string, confidenceScore?: number): Promise<{ success: boolean; data: any; message: string }> {
    const response = await api.post(`/system-tags/ticket/${ticketId}/assign`, {
      systemTagId,
      confidenceScore
    });
    return response.data;
  }

  /**
   * Ticket'tan system tag kaldır
   */
  async removeTagFromTicket(ticketId: string, systemTagId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/system-tags/ticket/${ticketId}/remove/${systemTagId}`);
    return response.data;
  }

  /**
   * AI ile ticket'ı analiz et ve tag ata
   */
  async analyzeTicket(ticketId: string, content: string, subject: string, organizationId: string, apiKey: string): Promise<AnalyzeTagResponse> {
    const response = await api.post<AnalyzeTagResponse>(`/system-tags/ticket/${ticketId}/analyze`, {
      content,
      subject,
      organizationId,
      apiKey
    });
    return response.data;
  }
}

export default new SystemTagsService();
